/*
 * File: ShareStory.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 1, 2025 at 5:24 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useMemo
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Services
import { recordView } from '../../../services/view';

// Components
import {
	LeavePageWidget,
	Meta,
	ShareStoryFlow,
} from '../../../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../../constants/base.constants';

// Styles
import * as S from './ShareStory.styles';


/**
 * Component
 */

const ShareStory = ({ meta: m }) => {

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Check if site is embedded
	const isEmbedded = useMemo(() => {
		if (typeof window !== 'undefined') {
			return window.self !== window.top;
		}
		return false;
	}, []);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageShareStory.metaTitle', { partnerName: partner.name }),
		description: t('pageShareStory.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Handle actions on component load
	useEffect(() => {

		// Record view
		recordView({
			source: SOURCES.SHARE,
			urlPath: window.location.pathname
		});

	}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Component Content */}
			<S.Wrapper>

				{/* Share Story Flow */}
				<ShareStoryFlow context="story" />

				{/* Leave Page Widget */}
				{!isEmbedded && <LeavePageWidget />}

			</S.Wrapper>
		</>
	);
};


/**
 * Configuration
 */

ShareStory.propTypes = {
	meta: PropTypes.shape()
};
ShareStory.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default ShareStory;
